import { useEffect, useState } from "react";
import { Button, ConfigProvider, Flex, InputNumber, Modal } from "antd";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { DEFAULT_PDF_ROWS, MAX_PDF_ROWS } from "pdf/layout";
import { PageCountFor } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

// Tinted-surface treatment for the "all rows" shortcut. Driven through
// ConfigProvider rather than inline styles so AntD's own hover/active states
// follow the tint instead of fighting it with the default grey tokens.
const allRowsButtonTheme = {
  components: {
    Button: {
      defaultBg: colors.navyTint,
      defaultColor: colors.navyDark,
      defaultBorderColor: "rgba(46, 63, 139, 0.20)",
      defaultHoverBg: "#D8DEF0",
      defaultHoverColor: colors.navyDark,
      defaultHoverBorderColor: "rgba(46, 63, 139, 0.38)",
      defaultActiveBg: "#D8DEF0",
      defaultActiveColor: colors.navyDark,
      defaultActiveBorderColor: "rgba(46, 63, 139, 0.38)",
    },
  },
};

interface PdfRowLimitModalProps {
  open: boolean;
  // Most rows this PDF can contain: the report's own row count, already
  // bounded by MAX_PDF_ROWS.
  available: number;
  // The report's true row count before that bound, so the dialog can say what
  // is being left out when the PDF ceiling is the binding constraint.
  total: number;
  // The pages a row count will produce, planned on the real rows; null until
  // the measurer (and any extra rows the download fetches) are ready.
  pagesFor: PageCountFor;
  preparing: boolean;
  loading: boolean;
  onCancel: () => void;
  onConfirm: (rows: number) => void;
}

// Asked before a report PDF renders whenever there are rows to choose from,
// so the admin sets how much of the table travels into the document. The
// page count is shown live because "how many pages" is usually the real
// question behind "how many rows"; it is the count the document will have
// (planned on the same rows with the same measurer), so it reads as a plain
// number, with "≈" only while the rows the download will fetch are not in
// hand. Taking every available row is one tap.
const PdfRowLimitModal = ({
  open,
  available,
  total,
  pagesFor,
  preparing,
  loading,
  onCancel,
  onConfirm,
}: PdfRowLimitModalProps) => {
  const max = Math.max(1, available);
  const [rows, setRows] = useState<number>(Math.min(DEFAULT_PDF_ROWS, max));

  // Reopening after the window/sort changed should offer the default again
  // rather than the previous session's number against a different table.
  useEffect(() => {
    if (open) setRows(Math.min(DEFAULT_PDF_ROWS, max));
  }, [open, max]);

  const safeRows = Math.min(Math.max(1, Math.floor(rows || 1)), max);
  const count = preparing ? null : pagesFor(safeRows);
  // A parenthetical after the unit, because the page count qualifies the
  // number the admin typed rather than standing beside it. "…" while the
  // measurer and rows load; nothing at all if they failed to load (the
  // download then reports its own error), never a made-up number.
  const pages = preparing
    ? `… ${translate("analyticsPage.export.rows_pages")}`
    : count === null
      ? null
      : `${count.exact ? "" : "≈ "}${count.pages} ${translate(
          count.pages === 1
            ? "analyticsPage.export.rows_page"
            : "analyticsPage.export.rows_pages",
        )}`;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={translate("analyticsPage.export.rows_title")}
      destroyOnClose
      footer={
        <ConfigProvider theme={modalButtonsTheme}>
          <Button onClick={onCancel}>{translate("analyticsPage.export.rows_cancel")}</Button>
          {/* Not before the rows are in hand: confirming mid-fetch would make
              the download fetch them a second time. */}
          <Button type="primary" loading={loading} disabled={preparing} onClick={() => onConfirm(safeRows)}>
            {translate("analyticsPage.export.pdf")}
          </Button>
        </ConfigProvider>
      }
    >
      <div style={{ fontSize: textPx(13), color: colors.ink2, marginBottom: 16 }}>
        {translate("analyticsPage.export.rows_desc")}
      </div>
      <Flex align="center" gap={10} wrap="wrap">
        <InputNumber
          min={1}
          max={max}
          step={10}
          value={rows}
          onChange={(value) => setRows(value ?? 1)}
          style={{ width: 110 }}
          autoFocus
        />
        {/* One phrase, so the count reads as a note on the number rather than
            a second label: the nested span keeps a plain word space and a
            lighter colour, and leaves "12 baris" alone when there is no
            count to show. */}
        <span style={{ fontSize: textPx(14), color: colors.ink2 }}>
          {translate("analyticsPage.export.rows_unit")}
          {pages && <span style={{ color: colors.ink3 }}> ({pages})</span>}
        </span>
      </Flex>
      {/* Navy-tinted so it reads as a tappable shortcut rather than a caption
          (same pairing as the dashboard quick actions: navyTint surface +
          navyDark text). Deliberately never disabled: greying it out once the
          field already holds the maximum is exactly what made it look inert,
          and re-clicking is a harmless no-op. */}
      <ConfigProvider theme={allRowsButtonTheme}>
        <Button onClick={() => setRows(max)} style={{ marginTop: 14, fontWeight: 600 }}>
          {`${translate("analyticsPage.export.rows_all")} (${max})`}
        </Button>
      </ConfigProvider>
      {/* The PDF ceiling, stated up front. When the report is bigger than the
          ceiling, say so and point at the Excel, which has no such limit. */}
      <div style={{ fontSize: textPx(12), color: colors.ink3, marginTop: 14 }}>
        {`${translate("analyticsPage.export.rows_cap_prefix")} ${MAX_PDF_ROWS} ${translate(
          "analyticsPage.export.rows_cap_suffix",
        )}`}
        {total > MAX_PDF_ROWS &&
          ` ${translate("analyticsPage.export.rows_excel_prefix")} ${total} ${translate(
            "analyticsPage.export.rows_excel_suffix",
          )}`}
      </div>
    </Modal>
  );
};

export default PdfRowLimitModal;
